import { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import buildMarketChartOptions from './MarketChartOptions';
import useMarketData from './hooks/useMarketData';
import { formatDateRange, MARKET_CHART_DAYS } from './marketDataUtils';

function MarketLineChartView(props) {
  const {
    title,
    chartId,
    endpoint,
    series: seriesProp,
    dates: datesProp,
    loading: loadingProp,
    error: errorProp
  } = props;

  const selfFetch = seriesProp === undefined && Boolean(endpoint);
  const fetched = useMarketData(selfFetch ? endpoint : null);

  const dates = datesProp ?? fetched.dates;
  const series = seriesProp ?? fetched.series;
  const loading = loadingProp ?? fetched.loading;
  const error = errorProp ?? fetched.error;

  const chartSurfaceRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(420);

  useEffect(() => {
    const element = chartSurfaceRef.current;
    if (!element) {
      return undefined;
    }

    const updateHeight = () => {
      const height = element.clientHeight;
      if (height > 0) {
        setChartHeight(height);
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const chartOptions = useMemo(
    () => buildMarketChartOptions({ chartId, dates, height: chartHeight }),
    [chartId, dates, chartHeight]
  );

  const dateRange = formatDateRange(dates);
  const hasData = series.length > 0;

  return (
    <div className="col-12">
      <div className="card border-light shadow-sm market-line-chart-card">
        <div className="card-header card-header-chart d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="h4 mb-0">{title}</div>
            <div className="text-muted small">Last {MARKET_CHART_DAYS} days · performance vs. start of period (%)</div>
          </div>
          {dateRange && (
            <div className="text-muted small">{dateRange}</div>
          )}
        </div>
        <div className="card-body market-line-chart-body">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading</span>
              </div>
            </div>
          ) : error || !hasData ? (
            <div className="d-flex justify-content-center align-items-center py-5 text-muted">
              Unable to load market data.
            </div>
          ) : (
            <div className="market-line-chart-surface" ref={chartSurfaceRef}>
              <Chart options={chartOptions} series={series} type="line" height={chartHeight} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarketLineChartView;
